/**
 * /guest/listings — the isolated area a verified share recipient lands on.
 *
 * Shows every listing currently shared with them, plus the numbers that make
 * the case for signing up: how much of the marketplace sits in the categories
 * they've already been shown, and how much of it they can't see.
 */
import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowRight, Ban, Building2, Inbox, Landmark, LayoutGrid, Lock, MapPin, Sparkles, TrendingUp,
} from "lucide-react";

import GuestShell from "@/components/GuestShell";
import { EmptyPanel, MetricTile, PagePanel, PageShell, PanelHeading } from "@/components/interior-page";
import { Button } from "@/components/ui/button";
import Loader from "@/components/ui/loader";
import { useToast } from "@/components/ui/toast";
import { useGuest } from "@/contexts/GuestContext";
import { guestApi } from "@/lib/guestApi";
import { assetUrl } from "@/lib/api";
import { fmtCount } from "@/lib/utils";
import type { GuestListingCard, GuestOverview } from "@/types";

function money(value: number | null): string {
  if (value == null) return "Price on request";
  return `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function placeLabel(card: GuestListingCard): string | null {
  return [card.city_name, card.state_name].filter(Boolean).join(", ") || null;
}

export default function GuestListingsPage() {
  const { isVerified, name } = useGuest();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState<GuestListingCard[]>([]);
  const [overview, setOverview] = useState<GuestOverview | null>(null);

  useEffect(() => {
    if (!isVerified) return;
    let cancelled = false;
    Promise.all([
      guestApi.get<GuestListingCard[]>("/guest/listings"),
      guestApi.get<GuestOverview>("/guest/overview"),
    ])
      .then(([listingCards, stats]) => {
        if (cancelled) return;
        setCards(listingCards);
        setOverview(stats);
      })
      .catch(() => {
        if (!cancelled) toast.error("We couldn't load the listings shared with you.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isVerified, toast]);

  // Session gone (expired, revoked, or never started) — the link is the only
  // way back in, and only the mailbox owner can complete it.
  if (!isVerified) return <Navigate to="/" replace />;

  const locked = overview ? Math.max(overview.interest_area_listings - overview.available_count, 0) : 0;

  return (
    <GuestShell>
      <PageShell centered>
        <section className="rounded-[1.85rem] border border-border/80 bg-card px-6 py-8 shadow-card sm:px-8">
          <div className="public-pill inline-flex">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            Shared with you
          </div>
          <h1 className="mt-4 font-display text-3xl leading-tight text-foreground sm:text-4xl">
            {name ? `Welcome, ${name}` : "Your shared listings"}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-7 text-muted-foreground sm:text-base">
            These opportunities were shared with you personally by a BusinessLocating advisor. Open any
            listing to see the full details.
          </p>
        </section>

        {loading ? (
          <Loader className="min-h-[40vh]" />
        ) : (
          <>
            {overview && (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <MetricTile label="Shared with you" value={overview.shared_count} icon={Inbox} />
                <MetricTile label="Available to view" value={overview.available_count} icon={LayoutGrid} tone="emerald" />
                <MetricTile
                  label="In your interest areas"
                  value={fmtCount(overview.interest_area_listings)}
                  icon={TrendingUp}
                  tone="amber"
                />
                <MetricTile
                  label="Listings on the platform"
                  value={fmtCount(overview.total_platform_listings)}
                  icon={Building2}
                />
              </div>
            )}

            {/* Listings */}
            <PagePanel>
              <PanelHeading
                title="Listings shared with you"
                description={
                  cards.length === 1
                    ? "1 listing has been shared with your email address."
                    : `${cards.length} listings have been shared with your email address.`
                }
              />
              {cards.length === 0 ? (
                <EmptyPanel
                  icon={Inbox}
                  title="Nothing shared right now"
                  description="Listings shared with your email address will appear here. If you were expecting something, ask your advisor to share it again."
                />
              ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {cards.map((card) => {
                    const image = assetUrl(card.listing_image_url);
                    const body = (
                      <div
                        className={`flex h-full flex-col overflow-hidden rounded-xl border border-border/75 bg-card shadow-sm transition-shadow duration-200 ${
                          card.is_available ? "hover:shadow-card" : "opacity-70"
                        }`}
                      >
                        <div className="relative h-36 w-full bg-muted/50">
                          {image ? (
                            <img src={image} alt="" className="h-full w-full object-cover" />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-muted-foreground/40">
                              <Building2 className="h-9 w-9" />
                            </div>
                          )}
                          {!card.is_available && (
                            <div className="absolute inset-0 flex items-center justify-center bg-background/75 px-4 text-center">
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-card px-3 py-1.5 text-xs font-bold text-muted-foreground shadow-sm">
                                <Ban className="h-3.5 w-3.5" />
                                No longer available
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="flex flex-1 flex-col p-4">
                          {card.category_name && (
                            <div className="text-[0.68rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                              {card.category_name}
                            </div>
                          )}
                          <h3 className="mt-1.5 line-clamp-2 text-base font-bold leading-snug text-foreground">
                            {card.title}
                          </h3>
                          {placeLabel(card) && (
                            <div className="mt-1.5 flex items-center gap-1 text-xs text-muted-foreground">
                              <MapPin className="h-3.5 w-3.5 shrink-0" />
                              {placeLabel(card)}
                            </div>
                          )}
                          {card.overview && (
                            <p className="mt-2.5 line-clamp-2 text-sm leading-6 text-muted-foreground">
                              {card.overview}
                            </p>
                          )}

                          <div className="mt-auto pt-4">
                            {card.is_available ? (
                              <>
                                <div className="text-lg font-extrabold tracking-tight text-foreground">
                                  {money(card.asking_price)}
                                </div>
                                {card.shared_by && (
                                  <div className="mt-1 text-xs text-muted-foreground">
                                    Shared by {card.shared_by}
                                  </div>
                                )}
                              </>
                            ) : (
                              <p className="text-xs leading-5 text-muted-foreground">
                                {card.unavailable_reason || "This listing is no longer available."}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );

                    return card.is_available ? (
                      <Link key={card.id} to={`/guest/listings/${card.id}`} className="h-full">
                        {body}
                      </Link>
                    ) : (
                      <div key={card.id} className="h-full cursor-not-allowed">
                        {body}
                      </div>
                    );
                  })}
                </div>
              )}
            </PagePanel>

            {/* Conversion */}
            {overview && (
              <PagePanel className="border-primary/25 bg-primary/[0.04]">
                <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-primary/12 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-primary">
                      <Sparkles className="h-3.5 w-3.5" />
                      There&rsquo;s more
                    </div>
                    <h2 className="mt-3 text-2xl font-bold tracking-tight text-foreground">
                      {locked > 0
                        ? `${fmtCount(locked)} more ${locked === 1 ? "business" : "businesses"} in the sectors you're already looking at`
                        : "See every business on the platform"}
                    </h2>
                    <p className="mt-2 max-w-xl text-sm leading-7 text-muted-foreground">
                      Create a free account to browse the full marketplace, save searches, and get
                      notified the moment a matching business is listed. No card required to start.
                    </p>
                    <div className="mt-5 flex flex-col gap-2.5 sm:flex-row">
                      <Button asChild size="lg">
                        <Link to="/register">
                          Create a free account
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      </Button>
                      <Button asChild variant="outline" size="lg">
                        <Link to="/sign-in">I already have an account</Link>
                      </Button>
                    </div>
                  </div>

                  {overview.categories.length > 0 && (
                    <div className="rounded-xl border border-border/75 bg-card p-4">
                      <div className="flex items-center gap-2 text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                        <Landmark className="h-3.5 w-3.5" />
                        Your interest areas
                      </div>
                      <ul className="mt-3 space-y-2.5">
                        {overview.categories.slice(0, 5).map((c) => (
                          <li key={c.category_id ?? c.category_name} className="flex items-center justify-between gap-3">
                            <span className="truncate text-sm font-semibold text-foreground">
                              {c.category_name}
                            </span>
                            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs font-bold text-muted-foreground">
                              <Lock className="h-3 w-3" />
                              {fmtCount(c.platform_count)}
                            </span>
                          </li>
                        ))}
                      </ul>
                      <p className="mt-3 border-t border-border/60 pt-3 text-xs leading-5 text-muted-foreground">
                        Live listings on BusinessLocating in these sectors.
                      </p>
                    </div>
                  )}
                </div>
              </PagePanel>
            )}
          </>
        )}
      </PageShell>
    </GuestShell>
  );
}
